import { Box, Flex, useColorMode, useColorModeValue } from '@chakra-ui/react';
import {
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  MoonIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { Avatar, Text } from '@main/complykit';
import { BackofficeDrawerType } from '@main/shared/utils';
import {
  DrawerContentMap,
  Header,
  menuItem,
  ModalProvider,
  PrimaryLayout,
  SidebarMenu,
  useDrawerContent,
} from '@main/ui';
import { useSignOut, useUserData } from '@nhost/react';
import { getRouteApi } from '@tanstack/react-router';
import { ReactNode, useCallback, useMemo } from 'react';

import OrganizationDrawer from '../features/organization/drawer';
import { UserDrawer } from '../features/user/drawer';
import { userSignedOut } from '../features/user/slice';
import { useBackofficeDispatch } from '../hooks/redux-toolkit-hooks';

const rootRouteApi = getRouteApi('/auth');

const drawers: DrawerContentMap<NonNullable<BackofficeDrawerType['drawerEntity']>> = {
  organization: ({ drawerEntityId }) => ({
    content: <OrganizationDrawer orgId={drawerEntityId} />,
  }),
  user: ({ drawerEntityId }) => ({ content: <UserDrawer userId={drawerEntityId} /> }),
};

function useAuthenticatedDrawer() {
  const { drawerContent } = useDrawerContent({
    useSearch: () =>
      rootRouteApi.useSearch({
        select: ({ drawerEntity, drawerEntityId }) => ({ drawerEntity, drawerEntityId }),
      }),
    drawers,
  });

  return drawerContent;
}

export default function SidebarAuthenticatedLayout({ children }: { children: ReactNode }) {
  const { menuItems } = useAuthSidebarMenu();
  const headerMenu = useAuthHeaderMenu();
  const logoColor = useColorModeValue('gray.700', 'gray.100');

  const drawerContent = useAuthenticatedDrawer();

  return (
    <ModalProvider>
      <PrimaryLayout
        sidebar={({ isSidebarCollapsed, onSidebarClose }) => (
          <Flex h="full" direction="column">
            <Box overflowY="auto" flex={1} mb={4}>
              <Box px={3} py={5}>
                <Flex minWidth={0} grow={1} alignItems="center" gap={3}>
                  <Avatar name="Backoffice" size="md" shape="square" isDecorative />
                  <Flex
                    direction="column"
                    grow={1}
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    <Text weight="bold" color="inherit" isTruncated>
                      Backoffice
                    </Text>
                  </Flex>
                </Flex>
              </Box>
              <SidebarMenu
                items={menuItems}
                onClick={onSidebarClose}
                isSidebarCollapsed={isSidebarCollapsed}
              />
            </Box>
          </Flex>
        )}
        header={({ logo, onSidebarOpen }) => (
          <Header logo={logo} onSidebarToggle={onSidebarOpen} {...headerMenu} />
        )}
        drawer={drawerContent}
      >
        {children}
      </PrimaryLayout>
    </ModalProvider>
  );
}

const useAuthSidebarMenu = () => {
  const menuItems = [
    menuItem({ label: 'Organizations', icon: BuildingOfficeIcon, to: '/organizations' }),
    menuItem({ label: 'Users', icon: UsersIcon, to: '/users' }),
  ];

  return { menuItems };
};

const useAuthHeaderMenu = () => {
  const user = useUserData();
  const { signOut } = useSignOut();
  const { colorMode, toggleColorMode } = useColorMode();
  const dispatch = useBackofficeDispatch();
  const textColor = useColorModeValue('gray.600', 'gray.300');

  const handleSignout = useCallback(() => {
    signOut();
    dispatch(userSignedOut());
  }, [dispatch, signOut]);

  const menuContent = <Avatar name={user?.displayName ?? ''} size="md" />;

  const menuMainItem = (
    <Flex alignItems="center" pl="4" py="2" pr="8" mb="2">
      <Avatar name={user?.displayName ?? ''} size="md" isDecorative />
      <Flex
        ml="3"
        flexDirection="column"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        <Text size="sm" weight="medium">
          {user?.displayName}
        </Text>
        <Text size="xs" color="muted">
          {user?.email}
        </Text>
      </Flex>
    </Flex>
  );

  const menuItems = useMemo(
    () => [
      {
        label: 'Dark mode',
        showSwitch: true,
        isSwitchChecked: colorMode === 'dark',
        onSwitchChange: toggleColorMode,
        icon: MoonIcon,
      },
      {
        label: 'Logout',
        onClick: handleSignout,
        icon: ArrowRightOnRectangleIcon,
      },
    ],
    [colorMode, handleSignout, toggleColorMode],
  );

  return { menuContent, menuMainItem, menuItems };
};
